import React, { useEffect, useRef, useState } from 'react';
import { WALL_LINE_PRESETS, nearestWallPreset } from '../../canvasConstants';

const fieldStyle: React.CSSProperties = {
  backgroundColor: '#111118',
  border: '1px solid #2e2e40',
  color: '#f0f0f5',
};

interface WallLinePickerProps {
  thickness: number;
  onChange: (px: number) => void;
}

/** Wall, cabinet, and counter stroke weights. */
export const WallLinePicker: React.FC<WallLinePickerProps> = ({ thickness, onChange }) => {
  const active = nearestWallPreset(thickness).id;
  return (
    <div className="flex items-center gap-1" role="group" aria-label="Line weight">
      {WALL_LINE_PRESETS.map((preset) => {
        const on = preset.id === active;
        return (
          <button
            key={preset.id}
            type="button"
            title={preset.description}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onChange(preset.thickness)}
            className="flex items-center gap-1.5 px-2 py-1 rounded text-xs"
            style={{
              backgroundColor: on ? 'rgba(255,76,76,0.15)' : '#252532',
              color: on ? '#FF4C4C' : '#f0f0f5',
              border: `1px solid ${on ? '#FF4C4C' : '#2e2e40'}`,
            }}
          >
            <span
              aria-hidden
              style={{
                display: 'inline-block',
                width: 16,
                height: preset.id === 'wall' ? 5 : preset.id === 'cabinet' ? 3 : 1.5,
                background: 'currentColor',
                borderRadius: 1,
              }}
            />
            {preset.label}
          </button>
        );
      })}
    </div>
  );
};

interface WallLengthControlProps {
  value: string;
  onValueChange: (value: string) => void;
  pointCount: number;
  onCommit: (metres: number) => void;
  onUndoPoint: () => void;
  canUndo: boolean;
}

/** Typed length for the wall segment currently being drawn. */
export const WallLengthControl: React.FC<WallLengthControlProps> = ({
  value,
  onValueChange,
  pointCount,
  onCommit,
  onUndoPoint,
  canUndo,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, [pointCount]);

  const submit = () => {
    const raw = inputRef.current?.value ?? value;
    const metres = parseFloat(raw);
    if (!Number.isFinite(metres) || metres <= 0 || metres > 200) return;
    onCommit(metres);
  };

  return (
    <form
      className="flex items-center gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <label className="text-xs whitespace-nowrap" style={{ color: '#8888aa' }} htmlFor="wall-length-input">
        Length
      </label>
      <input
        ref={inputRef}
        id="wall-length-input"
        type="text"
        inputMode="decimal"
        autoComplete="off"
        value={value}
        placeholder="0.00"
        onChange={(e) => onValueChange(e.target.value.replace(/[^0-9.]/g, ''))}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && value) {
            e.preventDefault();
            e.stopPropagation();
            onValueChange('');
          }
        }}
        className="w-[4.5rem] px-2 py-1 rounded text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[#FF4C4C]"
        style={fieldStyle}
        aria-label="Wall length in metres"
      />
      <span className="text-xs" style={{ color: '#8888aa' }}>
        m
      </span>
      <button type="submit" className="px-2.5 py-1 rounded text-xs font-medium" style={{ backgroundColor: '#FF4C4C', color: '#fff' }}>
        Set
      </button>
      <button
        type="button"
        onClick={onUndoPoint}
        disabled={!canUndo}
        className="px-2.5 py-1 rounded text-xs disabled:opacity-40"
        style={{ backgroundColor: '#252532', color: '#f0f0f5', border: '1px solid #2e2e40' }}
        title="Undo last point (Ctrl+Z)"
      >
        Undo point
      </button>
    </form>
  );
};

interface RoomSizePromptProps {
  onPlace: (widthM: number, heightM: number) => void;
  onCancel: () => void;
}

/** Width and height entry after a click, for square and rectangular rooms. */
export const RoomSizePrompt: React.FC<RoomSizePromptProps> = ({ onPlace, onCancel }) => {
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');
  const [square, setSquare] = useState(false);
  const widthRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    widthRef.current?.focus();
  }, []);

  const heightValue = square ? width : height;
  const w = parseFloat(width);
  const h = parseFloat(heightValue);
  const valid = Number.isFinite(w) && w > 0 && w <= 100 && Number.isFinite(h) && h > 0 && h <= 100;

  return (
    <form
      className="w-56 rounded-lg p-3 shadow-xl space-y-2"
      style={{ backgroundColor: '#1c1c28', border: '1px solid #2e2e40' }}
      onSubmit={(e) => {
        e.preventDefault();
        if (valid) onPlace(w, h);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          onCancel();
        }
      }}
    >
      <p className="text-xs font-medium" style={{ color: '#f0f0f5' }}>
        Room size
      </p>
      <div className="flex gap-2">
        <label className="flex-1 text-[11px]" style={{ color: '#8888aa' }}>
          Width (m)
          <input
            ref={widthRef}
            type="number"
            min={0.1}
            max={100}
            step={0.01}
            value={width}
            onChange={(e) => setWidth(e.target.value)}
            className="mt-1 w-full px-2 py-1.5 rounded text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[#FF4C4C]"
            style={fieldStyle}
          />
        </label>
        <label className="flex-1 text-[11px]" style={{ color: '#8888aa' }}>
          Height (m)
          <input
            type="number"
            min={0.1}
            max={100}
            step={0.01}
            value={heightValue}
            disabled={square}
            onChange={(e) => setHeight(e.target.value)}
            className="mt-1 w-full px-2 py-1.5 rounded text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[#FF4C4C] disabled:opacity-60"
            style={fieldStyle}
          />
        </label>
      </div>
      <label className="flex items-center gap-2 text-xs" style={{ color: '#c8c8da' }}>
        <input type="checkbox" checked={square} onChange={(e) => setSquare(e.target.checked)} />
        Square
      </label>
      <div className="flex justify-end gap-2 pt-1">
        <button type="button" onClick={onCancel} className="px-2 py-1 rounded text-xs" style={{ color: '#8888aa' }}>
          Cancel
        </button>
        <button
          type="submit"
          disabled={!valid}
          className="px-3 py-1 rounded text-xs font-medium disabled:opacity-40"
          style={{ backgroundColor: '#FF4C4C', color: '#fff' }}
        >
          Place room
        </button>
      </div>
    </form>
  );
};
